import React, { useState, useEffect, useRef } from "react";
// GRID LAYOUT
import "react-grid-layout/css/styles.css";
import GridLayout from "react-grid-layout";
// ANT DESIGN COMPONENTS
import { Spin, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateDetailViewLayout,
  updateTopViewLayout,
  updateStatusViewLayout,
  updateServiceViewLayout,
  updateHardCommands,
  updateAutoSortGrid,
  updateTerminalSplitRatio,
} from "../../../redux/monitoring/monitoringSlice";
// GRID RANGES
import gridRanges from "./GridRanges";
// COMMANDS
import commands from "./commands";
// COMPONENTS
import MonitoringBank from "./MonitoringBank";
import MonitoringWebSocket from "../socketConnection/MonitoringWebSocket";
import MonitoringWebSocketConnectionEmulator from "../socketConnection/MonitoringWebSocketConnectionEmulator";
import useUnclaimedCommandWatchdog from "../socketConnection/useUnclaimedCommandWatchdog";
import useWithheldCommandNotices from "../withheldCommands/useWithheldCommandNotices";
import TerminalPage from "../terminal/TerminalPage";
import TerminalSplitLayout from "../terminal/TerminalSplitLayout";
import ControlPanelLiftActions from "../ControlPanel/ControlPanelLiftActions";
import MonitoringDeviceView from "../deviceView/MonitoringDeviceView";
import ReplayFilesPanel from "../replayFiles/ReplayFilesPanel";
// AUTHORIZATION
import { MapViewInterface } from "../../../interfaces/MapView.interface";
import GETJwtToken from "../../../redux/authentication/authentication";
import GETJwtTokenSocket from "../../../redux/authentication/authenticationSocket";
// RUNTIME CONFIG
import useRuntimeConfig from "../../../config/useRuntimeConfig";
import { useLazyGetMapViewQuery } from "../../../redux/mapView/mapViewAPI";
import {
  updateMapViewArray,
  updateMapViewItem,
  updateSelectedOrganization,
} from "../../../redux/mapView/mapViewSlice";
// INTERFACES
import { ServerSocketConnectionInterface } from "../../../interfaces/EdgeManagement.interface";
import { RootState } from "../../../app/store";
import MonitoringMapView from "../mapView/MonitoringMapView";
import { MonitoringViewTypeEnum, LayoutInterface } from "../../../interfaces/Monitoring.interface";
// CONSTANTS
import { BLANK_LAYOUT } from "../../../constants/monitoring";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { resolveMapViewOrganizationId } from "./mapViewOrgResolution";
// SCOPE
import { useScopePredicate } from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

const MonitoringGrid = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const liftsAllowedPerColumn = 3;
  const [t] = useTranslation("global");
  const dispatch = useDispatch();

  // Single owner for the "no socket accepted this command" case. Mounted here,
  // beside the per-server sockets below, because this is the only component
  // that renders all of them — a watchdog inside a socket would fire once per
  // NON-matching instance on every successful command.
  useUnclaimedCommandWatchdog();
  const READ_FREQUENCY = 10;
  const SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_EDGE_SERVER;
  const { REACT_APP_WEB_SOCKET_OFFLINE } = useRuntimeConfig();
  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [layouts, setLayouts] = useState<LayoutInterface[]>([{ ...BLANK_LAYOUT }]);
  const [mainContainerWidth, setMainContainerWidth] = useState(0);
  const [gridBanks, setGridBanks] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [colWidth, setColWidth] = useState(102);
  const [colHeight, setColHeight] = useState(90);
  const [mapViewMultipleOrgsError, setMapViewMultipleOrgsError] = useState(false);
  const [serverSocketConnections, setServerSocketConnections] = useState<any>([]);
  const [pendingAutoSort, setPendingAutoSort] = useState(false);

  // Single owner for "VTS withheld a command" notices, for the same reason as
  // the watchdog above: every socket feeds one bus, and a listener per socket
  // would announce each verdict once per mounted server. The layouts name the
  // lift and floor in the notice.
  useWithheldCommandNotices(layouts);

  // ************************************************ */
  // SCOPE FILTERING ******************************* */
  // Returns a predicate that checks the VIEW scope for a given bank.
  // empty-rows ⇒ allow-all is handled inside useScopePredicate.
  const bankScopeAllows = useScopePredicate(PermissionAction.VIEW);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const organizationSelectMultipleData = useSelector(
    (state: RootState) => state.organization.organizationSelectMultipleData,
  );
  const enableDragAndDrop = useSelector((state: RootState) => state.monitoring.enableDragAndDrop);
  const refreshPage = useSelector((state: RootState) => state.monitoring.refreshPage);
  const topViewLayout = useSelector((state: RootState) => state.monitoring.topViewLayout);
  const detailViewLayout = useSelector((state: RootState) => state.monitoring.detailViewLayout);
  const statusViewLayout = useSelector((state: RootState) => state.monitoring.statusViewLayout);
  const serviceViewLayout = useSelector((state: RootState) => state.monitoring.serviceViewLayout);
  const monitoringGridType = useSelector((state: RootState) => state.monitoring.monitoringGridType);
  const socketConnection = useSelector((state: RootState) => state.monitoring.socketConnection);
  const openGlobalTerminal = useSelector((state: RootState) => state.monitoring.openGlobalTerminal);
  const terminalSplitView = useSelector((state: RootState) => state.monitoring.terminalSplitView);
  const terminalSplitOrientation = useSelector(
    (state: RootState) => state.monitoring.terminalSplitOrientation,
  );
  const terminalSplitRatio = useSelector((state: RootState) => state.monitoring.terminalSplitRatio);
  const openReplayPanel = useSelector((state: RootState) => state.monitoring.openReplayPanel);
  const replayFilterBankId = useSelector((state: RootState) => state.monitoring.replayFilterBankId);
  const replayScannedBankIds = useSelector(
    (state: RootState) => state.monitoring.replayScannedBankIds,
  );
  const autoSortGrid = useSelector((state: RootState) => state.monitoring.autoSortGrid);
  const previewFaultServiceMode = useSelector(
    (state: RootState) => state.monitoring.previewFaultServiceMode,
  );
  const theme = useSelector((state: RootState) => state.home.theme);

  // ************************************************ */
  // REFS ******************************************* */
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const mainContainerWidthRef = useRef(0);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetMapView, { data: dataGetMapView }] = useLazyGetMapViewQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const readHardcodedCommands = () => {
    if (process.env.REACT_APP_ENVIRONMENT === "DEVELOPMENT") {
      dispatch(updateHardCommands(commands.DEVELOPMENT));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "STAGING") {
      dispatch(updateHardCommands(commands.STAGING));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "PRODUCTION") {
      dispatch(updateHardCommands(commands.PRODUCTION));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "OFFLINE") {
      dispatch(updateHardCommands(commands.OFFLINE));
    }
    if (process.env.REACT_APP_ENVIRONMENT === "LOCAL") {
      dispatch(updateHardCommands(commands.DEVELOPMENT));
    }
  };

  const getInitialData = () => {
    readHardcodedCommands();
  };

  const getMapViewData = async (orgId: string) => {
    const token = await GETJwtToken();
    const BODY: any = {
      org_id: orgId,
    };
    triggerGetMapView({
      token,
      body: BODY,
    });
  };

  const displayIsLoading = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const layoutChange = (newLayout: any) => {
    // Never persist a layout react-grid-layout had to SHRINK to fit.
    //
    // RGL calls `onLayoutChange` for its own corrections, not just for user
    // drags, and `correctBounds` (utils.js) rewrites any item wider than the
    // grid:
    //
    //     if (l.x + l.w > bounds.cols) l.x = bounds.cols - l.w;
    //     if (l.x < 0) { l.x = 0; l.w = bounds.cols; }
    //
    // `cols` here is `mainContainerWidth / getEffectiveColWidth()`, so while the
    // container sits at the `min-width: 100px` floor of `.monitoring__100pxBox`
    // — first paint, a collapsed split pane, a narrow window — EVERY card is
    // wider than the grid and comes back as `{x: 0, w: cols}`. Persisting that
    // wrote the collapse into `detailViewLayout`, `generateInitialGrid` then
    // took its saved-layout branch and replayed it at every width, and the
    // dashboard stayed one narrow column of cards whose lift cells were too
    // small to click. Widening did not undo it and neither did a reload; saving
    // the grid made it permanent for that user.
    //
    // A clamped item is recognisable without recomputing anything: it is
    // NARROWER than the width this component asked for. A real drag only moves
    // items, and a real resize goes through `isResizable`, which is off unless
    // `enableDragAndDrop` is on.
    const intendedWidths = new Map(
      (layouts || []).map((item: LayoutInterface) => [item.i, item.w]),
    );
    const wasClampedToFit = (newLayout || []).some((item: any) => {
      const intended = intendedWidths.get(item.i);
      return typeof intended === "number" && item.w < intended;
    });
    if (wasClampedToFit) return;
    const copy = structuredClone(newLayout);
    if (monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) {
      dispatch(updateTopViewLayout(copy));
    } else if (monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW) {
      dispatch(updateStatusViewLayout(copy));
    } else if (monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW) {
      dispatch(updateDetailViewLayout(copy));
    } else if (monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW) {
      dispatch(updateServiceViewLayout(copy));
    }
  };

  const calculateCardWidth = (totalLifts: number, liftsAllowed: number, bankType: string) => {
    let carWeight = 5;
    if (bankType === "GPIO") {
      return 90;
    }
    if (totalLifts === 0) {
      return 45;
    }
    gridRanges.data4.forEach((range: any) => {
      if (totalLifts === range.units) {
        carWeight = range.cardWidth;
      }
    });
    return carWeight;
  };

  const calculateCardHeight = (
    totalFloors: number,
    totalLifts: number,
    bankType: string,
    gridType: MonitoringViewTypeEnum,
    actions: any,
  ) => {
    let cardHeight = 1;
    // *********************************
    if (bankType === "GPIO") {
      let totalToggle = 0;
      let totalHoldback = 0;
      let totalTurnback = 0;
      let totalToggleTimer = 0;
      actions.map((action: any) => {
        if (action && action.button_type === "TOGGLE") {
          totalToggle += 1;
        } else if (action && action.button_type === "HOLDBACK") {
          totalHoldback += 1;
        } else if (action && action.button_type === "TURNBACK") {
          totalTurnback += 1;
        } else if (action && action.button_type === "TOGGLETIMER") {
          totalToggleTimer += 1;
        }
        return true;
      });
      return totalToggle * 5 + totalHoldback * 5 + totalTurnback * 5 + totalToggleTimer * 5;
    }
    //
    if (gridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW) {
      if (bankType === "ESCALATOR" || bankType === "MOVINGWALK") {
        return 9.5;
      }
      if (totalLifts <= 3) {
        return 9.7;
      }
      return totalLifts / 2;
    }
    //
    if (gridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) {
      if (bankType === "ESCALATOR" || bankType === "MOVINGWALK") {
        return 9.2;
      }
      return 9.5;
    }
    if (gridType === MonitoringViewTypeEnum.STATUS_VIEW) {
      if (bankType === "ESCALATOR" || bankType === "MOVINGWALK") {
        return 8.2;
      }
      return 8.2;
    }
    if (totalLifts === 0) {
      cardHeight = 8;
    } else {
      if (bankType === "ESCALATOR" || bankType === "MOVINGWALK") {
        return 9.7;
      }
      gridRanges.data3.forEach((range: any) => {
        if (totalFloors === range.floors) {
          cardHeight = range.cardHeight;
        }
      });
    }
    return cardHeight;
  };

  const TYPE_ORDER: Record<string, number> = {
    ELEVATOR: 0,
    ESCALATOR: 1,
    MOVINGWALK: 2,
    GPIO: 3,
  };

  const getEffectiveColWidth = () => {
    if (monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) return 6;
    if (monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW) return 5;
    if (monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW) return 5;
    if (monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW) return 5;
    if (monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW) return 124;
    return colWidth;
  };

  const autoSortLayouts = (sortMode: string) => {
    if (!layouts || layouts.length <= 1) return;
    if (mainContainerWidth <= 0) return;
    displayIsLoading();
    const effectiveColWidth = getEffectiveColWidth();
    const totalCols = Math.max(1, Math.floor(mainContainerWidth / effectiveColWidth));
    const items = structuredClone(layouts) as LayoutInterface[];

    if (sortMode === "byTypeAligned") {
      const typeGroups: Record<string, LayoutInterface[]> = {};
      items.forEach((item: LayoutInterface) => {
        const type = item.type || "OTHER";
        if (!typeGroups[type]) typeGroups[type] = [];
        typeGroups[type].push(item);
      });
      const sortedTypes = Object.keys(typeGroups).sort(
        (a, b) => (TYPE_ORDER[a] ?? 99) - (TYPE_ORDER[b] ?? 99),
      );
      sortedTypes.forEach((type) => {
        typeGroups[type].sort((a, b) => (a.floorCount || 0) - (b.floorCount || 0));
      });
      let currentY = 0;
      const result: LayoutInterface[] = [];
      sortedTypes.forEach((type) => {
        const groupItems = typeGroups[type];
        let idx = 0;
        while (idx < groupItems.length) {
          let currentX = 0;
          let rowMaxH = 0;
          const rowItems: { item: LayoutInterface; x: number }[] = [];
          while (idx < groupItems.length) {
            const item = groupItems[idx];
            if (currentX + item.w > totalCols && currentX > 0) break;
            rowItems.push({ item, x: currentX });
            currentX += item.w;
            rowMaxH = Math.max(rowMaxH, item.h);
            idx += 1;
          }
          const rowY = currentY;
          rowItems.forEach(({ item, x }) => {
            result.push({ ...item, x, y: rowY, h: rowMaxH });
          });
          currentY += rowMaxH;
        }
      });
      setLayouts(result);
      layoutChange(result);
      return;
    }

    if (sortMode === "alphabetical") {
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortMode === "byType") {
      items.sort((a, b) => {
        const typeA = TYPE_ORDER[a.type] ?? 99;
        const typeB = TYPE_ORDER[b.type] ?? 99;
        if (typeA !== typeB) return typeA - typeB;
        return (a.floorCount || 0) - (b.floorCount || 0);
      });
    } else {
      items.sort((a, b) => {
        if (b.h !== a.h) return b.h - a.h;
        return b.w - a.w;
      });
    }

    let currentX = 0;
    let currentY = 0;
    let rowMaxH = 0;
    const sorted = items.map((item) => {
      if (currentX + item.w > totalCols && currentX > 0) {
        currentX = 0;
        currentY += rowMaxH;
        rowMaxH = 0;
      }
      const updated = { ...item, x: currentX, y: currentY };
      currentX += item.w;
      rowMaxH = Math.max(rowMaxH, item.h);
      return updated;
    });
    setLayouts(sorted);
    layoutChange(sorted);
  };

  const updateFormSelectSavedGrid = (newArr: any, oldLayout: any) => {
    const copy = structuredClone(newArr);
    let matchCount = 0;

    const getItemId = (item: any) =>
      String(item?.value ?? item?.idBank ?? item?.id ?? item?.i ?? "");

    copy.map((item: any, indexItem1: number) => {
      oldLayout.map((item2: any) => {
        if (getItemId(item) === String(item2?.i ?? "")) {
          const savedX = Number(item2?.x);
          const savedY = Number(item2?.y);
          const savedH = Number(item2?.h);
          if (Number.isFinite(savedX)) copy[indexItem1].x = savedX;
          if (Number.isFinite(savedY)) copy[indexItem1].y = savedY;
          if (Number.isFinite(savedH)) copy[indexItem1].h = savedH;
          matchCount += 1;
        }
        return true;
      });
      return true;
    });
    setLayouts(copy);
    if (matchCount === 0 && copy.length > 1) {
      setPendingAutoSort(true);
    } else {
      setPendingAutoSort(false);
    }
  };

  const generateInitialGrid = (containerWidth: number) => {
    displayIsLoading();
    let counterX = 0;
    let counterY = 0;
    const newArr: LayoutInterface[] = [];
    const banksCopy = structuredClone(gridBanks);
    // Default arrangement (only matters when no saved layout exists for this
    // view — saved layouts override x/y in updateFormSelectSavedGrid below).
    // Group same-type units (Elevator, Escalator, MovingWalk, etc.) so they
    // sit next to each other in the grid; within a type, sort by floor count
    // for a tidy ascending-height progression. The previous comparator
    // referenced `a.bank.floorCount`, but the iterator below treats each
    // entry as the bank itself (no `.bank` wrapper), so it never actually
    // sorted anything.
    const bankFloorCount = (b: any): number => {
      const raw = b?.floorCount ?? b?.floor_count ?? 0;
      const parsed = parseFloat(String(raw));
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const bankTypeKey = (b: any): string => String(b?.type ?? "");
    banksCopy.sort((a: any, b: any) => {
      const typeA = bankTypeKey(a);
      const typeB = bankTypeKey(b);
      if (typeA !== typeB) return typeA.localeCompare(typeB);
      return bankFloorCount(a) - bankFloorCount(b);
    });
    // The GridLayout below uses `cols = mainContainerWidth / colWidth`, with
    // `colWidth` as small as 5 for the dense view types — so the underlying
    // grid is ~178 columns wide for a typical 890px container, and a single
    // bank card occupies 60–80 of those columns. The original code advanced
    // `counterX` by `ceil(lifts/3) + 1` (a value of 2–3) and `counterY` by 1
    // per row, both of which are *tiny* compared to the actual `w`/`h` the
    // card is given. Every card overlapped every other card at column 0–4,
    // and react-grid-layout's vertical compaction shoved them all into a
    // single column — the "no grid at all" symptom Doug reported. Advance
    // `counterX` and `counterY` in the same units as `w`/`h` so the layout
    // hints actually correspond to non-overlapping cells.
    //
    // `getEffectiveColWidth()`, NOT the `colWidth` state. The two are different
    // numbers for every view type, and `getEffectiveColWidth` (above) handles all
    // five members of MonitoringViewTypeEnum — so its `return colWidth` fallback
    // is unreachable and these two divisors only ever agreed by coincidence.
    // Overhead is 6 and the dense views are 5, against a `colWidth` default of
    // 102; and switching away from map view runs this function with `colWidth`
    // still 124, because the [monitoringGridType] effect regenerates the grid
    // before `setColWidth` has committed.
    //
    // Dividing by 102 while the GridLayout below divides by 6 made
    // `colsAvailable` about seventeen times too small — 13 columns for a 1371px
    // container, when the grid actually has 228. `cardW` comes from
    // `gridRanges.data4` and is 45-85 for a normal bank, so
    // `counterX + cardW > colsAvailable` was true for EVERY card after the first
    // and the wrap below fired on each one, emitting all seven banks at x=0.
    //
    // In the usual path that output is then discarded: `setPendingAutoSort` runs
    // an auto-sort that already divides by `getEffectiveColWidth()`, so the
    // stacking was repaired before it was rendered, and reverting this line alone
    // does not change what the dashboard shows. It is fixed because the placement
    // hints are wrong, not because they were what collapsed the grid — that was
    // `layoutChange` persisting react-grid-layout's clamp, above.
    const colsAvailable = Math.max(
      1,
      Math.floor(containerWidth / Math.max(1, getEffectiveColWidth())),
    );
    // Aim for a roughly-square arrangement: target columns ≈ √N. A force-wrap
    // ensures the grid doesn't stretch into one long row even when the
    // container is wide enough to fit everything horizontally.
    const targetCols = Math.max(1, Math.ceil(Math.sqrt(banksCopy.length)));
    let itemsInRow = 0;
    let rowMaxH = 0;
    banksCopy.map((bank: any) => {
      const totalLifts = bank && bank.lifts ? bank.lifts.length : 0;
      const totalFloors = bank?.floorCount ?? bank?.floor_count ?? 0;
      // Sort floors — cascader JSON uses snake_case "bank_floors", FlatBuffer uses camelCase "bankFloors"
      let floorsSorted = [];
      const rawFloors = bank?.bankFloors ?? bank?.bank_floors;
      if (rawFloors && rawFloors.length > 0) {
        floorsSorted = rawFloors.sort(
          (a: any, b: any) =>
            (b.floorIndex ?? b.floor_index ?? 0) - (a.floorIndex ?? a.floor_index ?? 0),
        );
      }
      // Create floors in use array
      const floorsInUse: any = [];
      const cardW = calculateCardWidth(totalLifts, liftsAllowedPerColumn, bank.type);
      const cardH = calculateCardHeight(
        totalFloors,
        totalLifts,
        bank.type,
        monitoringGridType,
        bank && bank.actions ? bank.actions : [],
      );
      // Wrap *before* placing if this card wouldn't fit on the current row
      // (either it overflows the container width or we've already filled the
      // square-grid quota for this row).
      if (counterX > 0 && (counterX + cardW > colsAvailable || itemsInRow >= targetCols)) {
        counterX = 0;
        counterY += rowMaxH;
        rowMaxH = 0;
        itemsInRow = 0;
      }
      newArr.push({
        ...bank,
        bankFloors: floorsSorted,
        fbEnabled: bank.fbEnabled,
        floorsInUse,
        i: bank.id,
        x: counterX,
        y: counterY,
        w: cardW,
        h: cardH,
        l: totalLifts,
        f: totalFloors,
        floor: 3,
        startFloorNo: bank.startFloorNo ?? bank.start_floor_no,
      });
      counterX += cardW;
      rowMaxH = Math.max(rowMaxH, cardH);
      itemsInRow += 1;
      return true;
    });
    if (
      topViewLayout &&
      topViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, topViewLayout);
    } else if (
      detailViewLayout &&
      detailViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, detailViewLayout);
    } else if (
      statusViewLayout &&
      statusViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, statusViewLayout);
    } else if (
      serviceViewLayout &&
      serviceViewLayout.length > 0 &&
      monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW
    ) {
      updateFormSelectSavedGrid(newArr, serviceViewLayout);
    } else {
      setLayouts(newArr);
      if (newArr.length > 1) {
        setPendingAutoSort(true);
      } else {
        setPendingAutoSort(false);
      }
    }
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    getInitialData();
  }, []);

  useEffect(() => {
    if (gridBanks && gridBanks.length > 0) {
      const elementId = document.getElementById("mainContainer");
      const elementId2 = document.getElementById("mainContainer2");
      if (elementId) {
        setMainContainerWidth(elementId.clientWidth);
      } else if (elementId2) {
        generateInitialGrid(elementId2.clientWidth);
      }
    }
  }, [gridBanks]);

  useEffect(() => {
    if (organizationSelectMultipleData && organizationSelectMultipleData.length > 0) {
      // Drop banks flagged as not visible — covers fresh cascader selections
      // AND restored favorite grids where a bank was visible at save time but
      // has since been hidden. Treat undefined/null as visible for legacy data.
      // Also apply the per-bank VIEW scope: empty grant rows ⇒ allow-all.
      const visibleBanks = organizationSelectMultipleData.filter((bank: any) => {
        if (bank.visible === false) return false;
        const bankId: string = bank.value ?? bank.idBank ?? bank.id ?? "";
        return bankScopeAllows({ bankId });
      });
      setGridBanks(visibleBanks);
      if (monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW) {
        const mapViewOrganizationId = resolveMapViewOrganizationId(organizationSelectMultipleData);
        if (mapViewOrganizationId) {
          setMapViewMultipleOrgsError(false);
          getMapViewData(mapViewOrganizationId);
          dispatch(updateSelectedOrganization(mapViewOrganizationId));
        } else {
          setMapViewMultipleOrgsError(true);
        }
      }
    } else {
      setGridBanks([]);
      setLayouts([]);
    }
  }, [organizationSelectMultipleData, monitoringGridType]);

  useEffect(() => {
    const startMapView = dataGetMapView?.data?.find((m: MapViewInterface) => m.start === true);
    dispatch(updateMapViewItem(startMapView || dataGetMapView?.data[0]));
    dispatch(updateMapViewArray(dataGetMapView?.data));
  }, [dataGetMapView]);

  useEffect(() => {
    if (refreshPage) {
      displayIsLoading();
    }
  }, [refreshPage]);

  useEffect(() => {
    if (autoSortGrid) {
      autoSortLayouts(autoSortGrid);
      dispatch(updateAutoSortGrid(""));
      setPendingAutoSort(false);
    }
  }, [autoSortGrid]);

  useEffect(() => {
    if (pendingAutoSort && layouts && layouts.length > 1 && mainContainerWidth > 0) {
      dispatch(updateAutoSortGrid("byTypeAligned"));
    }
  }, [pendingAutoSort, layouts, mainContainerWidth]);

  useEffect(() => {
    const elementId = document.getElementById("mainContainer");
    if (elementId && gridBanks && gridBanks.length > 0) {
      generateInitialGrid(elementId.clientWidth);
    }
    //
    if (monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) {
      setColHeight(1);
      setColWidth(6);
    } else if (monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW) {
      setColHeight(1);
      setColWidth(5);
    } else if (monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW) {
      setColHeight(1);
      setColWidth(5);
    } else if (monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW) {
      setColHeight(1);
      setColWidth(5);
    } else if (monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW) {
      setColHeight(90);
      setColWidth(124);
    }
  }, [monitoringGridType]);

  useEffect(() => {
    setServerSocketConnections([]);
    if (layouts && layouts.length > 0) {
      const serversId: ServerSocketConnectionInterface[] = [];
      const uniqueServerId: string[] = [];
      const firstLiftByBank: any = [];
      layouts.map((layout: LayoutInterface) => {
        if (layout && layout.lifts && layout.lifts[0]) {
          firstLiftByBank.push({
            serverId: layout.idServer,
            lift: layout.lifts[0],
          });
        }
        // Skip layouts with no server id — opening a WebSocket with
        // ?serverID= (empty) fails the upgrade and spams the console.
        if (
          layout.idServer &&
          layout.idServer !== "" &&
          !uniqueServerId.includes(layout.idServer)
        ) {
          serversId.push({
            id: layout.idServer,
            name: layout.nameServer,
            channelType: layout.typeChannel,
            firstLiftId: [],
            channelId: layout.channelId ?? layout.idChannel,
          });
          uniqueServerId.push(layout.idServer);
        }
        return true;
      });
      const copy: any = structuredClone(serversId);
      copy.map((item: any, index: number) => {
        const newArray: any = [];
        firstLiftByBank.map((item2: any) => {
          if (item2 && item2.serverId && item && item.id && item.id === item2.serverId) {
            newArray.push(item2.lift);
          }
          return true;
        });
        copy[index].firstLiftId = newArray;
        return true;
      });
      setServerSocketConnections(copy);
    }
  }, [layouts]);

  // Keep the ref in sync so the ResizeObserver closure always sees the latest width
  useEffect(() => {
    mainContainerWidthRef.current = mainContainerWidth;
  }, [mainContainerWidth]);

  // Recalculate grid width when the main container resizes (e.g. drawer
  // open/close, or a split-view divider drag).
  //
  // Observes the ref rather than `getElementById` and re-runs whenever the
  // grid changes place in the tree. Entering or leaving split view moves the
  // grid container under a different parent, which React commits as a new DOM
  // node — a one-shot effect would be left observing the detached old node and
  // the grid would keep whatever width it had when the terminal opened.
  useEffect(() => {
    const el = mainContainerRef.current;
    if (!el) return undefined;

    resizeObserverRef.current = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const newWidth = entry.contentRect.width;
        if (newWidth > 0 && newWidth !== mainContainerWidthRef.current) {
          window.requestAnimationFrame(() => setMainContainerWidth(newWidth));
        }
      }
    });
    resizeObserverRef.current.observe(el);

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [openGlobalTerminal, terminalSplitView, terminalSplitOrientation]);

  // Regenerate grid when both mainContainerWidth and gridBanks are available.
  // Watches both deps so a late-arriving gridBanks (after width is set) or
  // a late-arriving width (after gridBanks is set) both trigger generation.
  useEffect(() => {
    if (mainContainerWidth > 0 && gridBanks && gridBanks.length > 0) {
      generateInitialGrid(mainContainerWidth);
    }
  }, [mainContainerWidth, gridBanks]);

  // ************************************************ */
  // FILTERED LAYOUTS FOR REPLAY ******************** */
  const gridLayouts = (() => {
    if (!openReplayPanel) return layouts;
    if (replayFilterBankId) {
      return layouts.filter((l) => l.idBank === replayFilterBankId);
    }
    if (replayScannedBankIds.length > 0) {
      return layouts.filter((l) => replayScannedBankIds.includes(l.idBank));
    }
    return layouts;
  })();

  // ************************************************ */
  // RENDER FRAGMENTS ******************************* */
  // The terminal and the grid are built once here and then placed either into
  // the split layout or into their original stacked slots below. Sharing the
  // element definitions is what keeps split and non-split modes from drifting
  // apart as either one changes.
  const terminalNode = openGlobalTerminal ? <TerminalPage layouts={layouts} /> : null;

  const gridNode = (
    <div id="mainContainer" ref={mainContainerRef} className="monitoring__100pxBox">
      {mainContainerWidth > 0 && layouts && layouts.length > 0 && !pendingAutoSort && (
        <div>
          {monitoringGridType === MonitoringViewTypeEnum.MAP_VIEW && (
            <>
              {mapViewMultipleOrgsError ? (
                <div className="generalStyles__flexCenter mt-3">
                  <span className="mt-4">{t("monitoring.mapViewMultipleOrgsError")}</span>
                </div>
              ) : (
                <MonitoringMapView layouts={gridLayouts} />
              )}
            </>
          )}
          {monitoringGridType === MonitoringViewTypeEnum.DEVICE_STATUS_VIEW && (
            <MonitoringDeviceView layouts={gridLayouts} />
          )}
          {(monitoringGridType === MonitoringViewTypeEnum.EXPANDED_VIEW ||
            monitoringGridType === MonitoringViewTypeEnum.STATUS_VIEW ||
            monitoringGridType === MonitoringViewTypeEnum.OVER_HEAD_VIEW) && (
            <GridLayout
              className="layout"
              layout={gridLayouts}
              onLayoutChange={layoutChange}
              cols={Math.max(1, Math.floor(mainContainerWidth / getEffectiveColWidth()))}
              rowHeight={colHeight}
              autoSize
              width={mainContainerWidth}
              isResizable={enableDragAndDrop}
              margin={[20, 20]}
              containerPadding={[10, 10]}
              isDraggable={enableDragAndDrop}
            >
              {gridLayouts.map((layout: LayoutInterface) => (
                <div
                  id={layout.i}
                  key={layout.i}
                  style={{
                    border:
                      previewFaultServiceMode || enableDragAndDrop
                        ? "solid 1px hsl(217, 84%, 52%, 25%)"
                        : "",
                    borderRadius: "5px",
                  }}
                >
                  {/** *********************************************************** */}
                  {/** BODY */}
                  {layout.type === LiftType[LiftType.GPIO] ? (
                    <div style={{ padding: "10px" }}>
                      <ControlPanelLiftActions
                        isControlPanel={false}
                        bankId={layout.idBank}
                        bankName={layout.name}
                      />
                    </div>
                  ) : (
                    <>
                      {layout && layout.lifts && layout.lifts.length > 0 ? (
                        <div style={{ height: "100%" }}>
                          <MonitoringBank
                            bankId={layout.idBank}
                            channelId={layout.idChannel}
                            serverId={layout.idServer}
                            h={layout.h}
                            w={layout.w}
                            lifts={layout.lifts}
                            bankFloors={layout.bankFloors}
                          />
                        </div>
                      ) : (
                        <div
                          className={`monitoring__card__${theme} generalStyles__width100`}
                          style={{ height: "100%", display: "flex", flexDirection: "column" }}
                        >
                          <div className="monitoring__card__header">
                            <div className="generalStyles__flex">
                              <div className="generalStyles__textEllipsis">
                                <Tooltip placement="topLeft" title={layout.name}>
                                  <h6>{layout.name}</h6>
                                </Tooltip>
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span style={{ opacity: 0.5 }}>
                              {t("edgeManagement.lifts.noLiftsFound")}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </GridLayout>
          )}
        </div>
      )}
    </div>
  );

  // Split view keeps BOTH panes mounted: the grid stays live while the
  // Terminal is open, which is the entire point — an operator fires a command
  // from the Terminal and watches the dashboard react to it.
  const splitViewActive = openGlobalTerminal && terminalSplitView;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Spin spinning={isLoading}>
      {/** ************************************************** */}
      {/** MONITORING GLOBAL TERMINAL — full width unless split view is on */}
      <div id="mainContainer2">{!splitViewActive && terminalNode}</div>
      {/** ************************************************** */}
      {/** MONITORING REPLAY FILE */}
      {openReplayPanel && <ReplayFilesPanel layouts={layouts} />}
      {/** ************************************************** */}
      {/** EDGE SERVER CONNECTION */}
      {!openReplayPanel &&
        (process.env.REACT_APP_ENVIRONMENT === "DEVELOPMENT" ||
          process.env.REACT_APP_ENVIRONMENT === "PRODUCTION") && (
          <>
            {socketConnection === "EDGE" && (
              <div className="generalStyles__flexWrap">
                {serverSocketConnections.map(
                  (serverSocketConnection: ServerSocketConnectionInterface) => {
                    // Filter layouts for this specific server
                    const serverLayouts = layouts.filter(
                      (layout) => layout.idServer === serverSocketConnection.id,
                    );
                    return (
                      <div key={serverSocketConnection.id}>
                        <MonitoringWebSocket
                          serverId={serverSocketConnection.id}
                          serverName={serverSocketConnection.name}
                          channelId={serverSocketConnection.channelId}
                          layouts={serverLayouts}
                          createWebSocket={async () => {
                            const token = await GETJwtTokenSocket();
                            // Pass the token via Sec-WebSocket-Protocol subprotocol.
                            // The browser WS API cannot set Authorization headers on
                            // upgrade requests, so we use "bearer.<token>" as the
                            // subprotocol. The server echoes it back to complete the
                            // handshake. serverID is passed as a query param only
                            // (non-sensitive routing hint).
                            return new WebSocket(
                              `${SOCKET_URL}?serverID=${serverSocketConnection.id}`,
                              [`bearer.${token}`],
                            );
                          }}
                          respectReplayPanel
                          connectionLabel="EDGE (ONLINE)"
                        />
                      </div>
                    );
                  },
                )}
              </div>
            )}
          </>
        )}
      {/** *********************************************************** */}
      {/** OFFLINE SERVER CONNECTION */}
      {/*
       * NOT gated on `!openReplayPanel` (unlike the EDGE/EMULATOR mounts above
       * and below). The OFFLINE mount already passes `respectReplayPanel={false}`
       * and MonitoringWebSocket honours that prop internally at all three of its
       * replay-panel checkpoints (mount-time auto-connect, message dispatch, and
       * the reconnect timer), so an outer `!openReplayPanel` gate contradicts the
       * prop's evident intent: it unmounts the very component that was explicitly
       * told to ignore the replay panel.
       *
       * The contradiction is load-bearing, not cosmetic. Unmounting runs
       * MonitoringWebSocket's effect cleanup, which closes the socket, so
       * toggling "Show replay panel" ON silently killed live OFFLINE monitoring —
       * the dashboard froze at whatever batch had already arrived while wire data
       * kept moving. The replay panel is also the ONLY renderer of the raw
       * `floor.floorIndex` (MonitoringElevatorsFullView), which is the single
       * representation comparable 1:1 against the wire Position byte, so the
       * LobbyVision2 Roosevelt demo's motion assertion needs the panel ON *and*
       * the socket live at the same time. Ungating exactly this site is what
       * makes both possible; the panel keeps its own render slot above.
       */}
      {process.env.REACT_APP_ENVIRONMENT === "OFFLINE" && (
        <>
          {socketConnection === "OFFLINE" && (
            <div className="generalStyles__flexWrap">
              {serverSocketConnections.map(
                (serverSocketConnection: ServerSocketConnectionInterface) => {
                  // Filter layouts for this specific server
                  const serverLayouts = layouts.filter(
                    (layout) => layout.idServer === serverSocketConnection.id,
                  );
                  return (
                    <div key={serverSocketConnection.id}>
                      <MonitoringWebSocket
                        serverId={serverSocketConnection.id}
                        serverName={serverSocketConnection.name}
                        channelId={serverSocketConnection.channelId}
                        layouts={serverLayouts}
                        createWebSocket={async () => {
                          // Browsers send the JWT as a subprotocol because they cannot set headers.
                          const token = await GETJwtTokenSocket();
                          return new WebSocket(REACT_APP_WEB_SOCKET_OFFLINE, [`bearer.${token}`]);
                        }}
                        respectReplayPanel={false}
                        connectionLabel="OFFLINE"
                      />
                    </div>
                  );
                },
              )}
            </div>
          )}
        </>
      )}
      {/** *********************************************************** */}
      {/** EMULATOR CONNECTION */}
      {!openReplayPanel && process.env.REACT_APP_ENVIRONMENT === "STAGING" && (
        <>
          {socketConnection === "EMULATOR" && (
            <div className="generalStyles__flexWrap">
              <MonitoringWebSocketConnectionEmulator
                serverName="311 Wacker"
                readFrequency={READ_FREQUENCY}
              />
            </div>
          )}
        </>
      )}
      {/** *********************************************************** */}
      {/** MONITORING GRID — split alongside the Terminal, or on its own */}
      {splitViewActive && (
        <TerminalSplitLayout
          orientation={terminalSplitOrientation}
          ratio={terminalSplitRatio}
          onRatioChange={(next) => dispatch(updateTerminalSplitRatio(next))}
          first={gridNode}
          second={terminalNode}
        />
      )}
      {!splitViewActive && !openGlobalTerminal && gridNode}
    </Spin>
  );
};

export default MonitoringGrid;
